import {
  keepPreviousData,
  queryOptions,
  useQuery,
  useSuspenseQuery,
  type UseQueryResult,
  type UseSuspenseQueryResult,
} from '@tanstack/react-query'

import { isApiError } from '@/api/errors'
import { fundService } from '@/features/mutual-funds/api/fundService'
import { fundKeys } from '@/features/mutual-funds/api/keys'
import type {
  Fund,
  FundAnalyticsSummary,
  FundDividends,
  FundHoldings,
  FundIncomeStatement,
  FundListQuery,
  FundPeriod,
  FundSectorBreakdown,
  FundSectorCount,
  FundStockCount,
  FundTrends,
  FundTrendsQuery,
  IndustryHoldings,
  MonthlyHoldings,
  NavHistory,
  PopularStock,
  SectorCompany,
  StockHolders,
} from '@/schemas/mutual-funds'

/**
 * The hooks the Mutual funds screens call — one `queryOptions` factory per
 * endpoint (what a route loader hands `ensureRouteData`/`prefetchRouteData`),
 * and the hooks spread it.
 *
 * NOTHING HERE POLLS. Fund holdings are monthly filings, not market data:
 * no `refetchInterval`, no market-status gate.
 *
 * Every read keyed on a param a screen can change (a period, a category, a
 * sector, a fund) takes `keepPreviousData`, so a change dims the old rows
 * rather than blanking them — docs/route-loading.md, "Search params". The
 * `useSuspense…` variants are for a screen's MAIN read when no search param
 * keys it; the loader has already awaited the same factory.
 */

/**
 * An hour: filings land once a month, so a revisit inside the hour re-reads
 * the cache instead of the network. On the factory rather than the hook
 * because `prefetchRouteData` honours the query's OWN staleTime — the reason
 * `features/depth/api/queries.ts` gives for `LISTING_STALE_MS`.
 */
const FUND_DATA_STALE_MS = 60 * 60_000

// ── Directory ────────────────────────────────────────────────────────────────

/** Price vs NAV's main read, keyed on the category tab. */
export const fundListQueryOptions = (query: FundListQuery) =>
  queryOptions<Fund[]>({
    queryKey: fundKeys.list(query),
    queryFn: () => fundService.list(query),
    staleTime: FUND_DATA_STALE_MS,
  })

export const useFundList = (query: FundListQuery): UseQueryResult<Fund[]> =>
  useQuery({ ...fundListQueryOptions(query), placeholderData: keepPreviousData })

/** Every fund at once: a fund's page finds itself in it (mm-be has no one-fund read). */
export const useSuspenseFundDirectory = (): UseSuspenseQueryResult<Fund[]> =>
  useSuspenseQuery(fundListQueryOptions({}))

// ── Periods ──────────────────────────────────────────────────────────────────

/** The period select's options, newest first (Industry and Monthly holdings). */
export const fundPeriodsQueryOptions = () =>
  queryOptions<FundPeriod[]>({
    queryKey: fundKeys.periods(),
    queryFn: () => fundService.periods(),
    staleTime: FUND_DATA_STALE_MS,
  })

export const useSuspenseFundPeriods = (): UseSuspenseQueryResult<FundPeriod[]> =>
  useSuspenseQuery(fundPeriodsQueryOptions())

// ── Analytics ────────────────────────────────────────────────────────────────

/** Analytics' three tiles. */
export const fundSummaryQueryOptions = (period: string | null) =>
  queryOptions<FundAnalyticsSummary>({
    queryKey: fundKeys.summary(period),
    queryFn: () => fundService.summary(period),
    staleTime: FUND_DATA_STALE_MS,
  })

export const useSuspenseFundSummary = (
  period: string | null,
): UseSuspenseQueryResult<FundAnalyticsSummary> => useSuspenseQuery(fundSummaryQueryOptions(period))

/**
 * Funds by stock count — Analytics' first tab, the trends chart's default
 * funds (the top three), and the whole of Fund rankings.
 */
export const fundStockCountQueryOptions = (period: string | null) =>
  queryOptions<FundStockCount[]>({
    queryKey: fundKeys.stockCount(period),
    queryFn: () => fundService.stockCount(period),
    staleTime: FUND_DATA_STALE_MS,
  })

export const useSuspenseFundStockCount = (
  period: string | null,
): UseSuspenseQueryResult<FundStockCount[]> => useSuspenseQuery(fundStockCountQueryOptions(period))

/** The same list, for a block on a fund's page that must not hold the page up. */
export const useFundStockCount = (period: string | null): UseQueryResult<FundStockCount[]> =>
  useQuery(fundStockCountQueryOptions(period))

/** Analytics' "Most popular" tab. */
export const popularStocksQueryOptions = (period: string | null) =>
  queryOptions<PopularStock[]>({
    queryKey: fundKeys.popular(period),
    queryFn: () => fundService.popular(period),
    staleTime: FUND_DATA_STALE_MS,
  })

export const usePopularStocks = (period: string | null): UseQueryResult<PopularStock[]> =>
  useQuery({ ...popularStocksQueryOptions(period), placeholderData: keepPreviousData })

/** Analytics' "Sector diversity" tab. */
export const fundSectorCountsQueryOptions = (period: string | null) =>
  queryOptions<FundSectorCount[]>({
    queryKey: fundKeys.sectorCounts(period),
    queryFn: () => fundService.sectorCounts(period),
    staleTime: FUND_DATA_STALE_MS,
  })

export const useFundSectorCounts = (period: string | null): UseQueryResult<FundSectorCount[]> =>
  useQuery({ ...fundSectorCountsQueryOptions(period), placeholderData: keepPreviousData })

/**
 * One fund's sector split — the Sector diversity dialog. Silent until a fund
 * is named: an empty path segment is not a fund.
 */
export const fundSectorBreakdownQueryOptions = (fund: string, period: string | null) =>
  queryOptions<FundSectorBreakdown>({
    queryKey: fundKeys.sectorBreakdown(fund, period),
    queryFn: () => fundService.sectorBreakdown(fund, period),
    staleTime: FUND_DATA_STALE_MS,
  })

export const useFundSectorBreakdown = (
  fund: string,
  period: string | null,
): UseQueryResult<FundSectorBreakdown> =>
  useQuery({
    ...fundSectorBreakdownQueryOptions(fund, period),
    enabled: fund.length > 0,
    placeholderData: keepPreviousData,
  })

/**
 * The trends chart.
 *
 * `funds` is de-duplicated and sorted before it becomes the key and the
 * request, so ["NFCF","NIBLSF"] and ["NIBLSF","NFCF"] are one cache entry —
 * the chart draws a SET of funds. The backend answers series in the order
 * asked, so the response comes back alphabetical: a screen colours each line
 * by its symbol (its own selection order), never by its index in the
 * response.
 */
export const fundTrendsQueryOptions = (query: FundTrendsQuery) => {
  const normalised: FundTrendsQuery = {
    funds: [...new Set(query.funds)].sort(),
  }
  return queryOptions<FundTrends>({
    queryKey: fundKeys.trends(normalised),
    queryFn: () => fundService.trends(normalised),
    staleTime: FUND_DATA_STALE_MS,
  })
}

/** Silent with no funds picked — the backend would answer all 51 series. */
export const useFundTrends = (query: FundTrendsQuery): UseQueryResult<FundTrends> =>
  useQuery({
    ...fundTrendsQueryOptions(query),
    enabled: query.funds.length > 0,
    placeholderData: keepPreviousData,
  })

// ── Holdings ─────────────────────────────────────────────────────────────────

/** Industry holdings' donut, keyed on the period select. */
export const industryHoldingsQueryOptions = (period: string | null) =>
  queryOptions<IndustryHoldings>({
    queryKey: fundKeys.industryHoldings(period),
    queryFn: () => fundService.industryHoldings(period),
    staleTime: FUND_DATA_STALE_MS,
  })

export const useIndustryHoldings = (period: string | null): UseQueryResult<IndustryHoldings> =>
  useQuery({ ...industryHoldingsQueryOptions(period), placeholderData: keepPreviousData })

/**
 * One sector's companies. `sector` is the CODE; silent until there is one —
 * the unclassified slice has none, and the backend requires it.
 */
export const sectorCompaniesQueryOptions = (sector: string, period: string | null) =>
  queryOptions<SectorCompany[]>({
    queryKey: fundKeys.sectorCompanies(sector, period),
    queryFn: () => fundService.sectorCompanies(sector, period),
    staleTime: FUND_DATA_STALE_MS,
  })

export const useSectorCompanies = (
  sector: string,
  period: string | null,
): UseQueryResult<SectorCompany[]> =>
  useQuery({
    ...sectorCompaniesQueryOptions(sector, period),
    enabled: sector.length > 0,
    placeholderData: keepPreviousData,
  })

/** Industry holdings' All sectors table (the old Monthly holdings page), keyed on the period. */
export const monthlyHoldingsQueryOptions = (period: string | null) =>
  queryOptions<MonthlyHoldings>({
    queryKey: fundKeys.monthlyHoldings(period),
    queryFn: () => fundService.monthlyHoldings(period),
    staleTime: FUND_DATA_STALE_MS,
  })

export const useMonthlyHoldings = (
  period: string | null,
  { enabled = true }: { enabled?: boolean } = {},
): UseQueryResult<MonthlyHoldings> =>
  useQuery({ ...monthlyHoldingsQueryOptions(period), enabled, placeholderData: keepPreviousData })

// ── One fund, one stock (mm-be #372) ─────────────────────────────────────────

/**
 * A read that answers `null` on a 404, so its block simply does not draw: the fund or the stock
 * is not one mm-be knows, OR the endpoint is not deployed yet (mm-be's
 * feat/web-asks-funds-portfolio, in the spec ahead of its deploy). Either way there is nothing to
 * show, and a "Try again" would get the same 404. Any other failure still throws.
 */
const orNullOn404 = async <T>(read: () => Promise<T>): Promise<T | null> => {
  try {
    return await read()
  } catch (error) {
    if (isApiError(error) && error.status === 404) return null
    throw error
  }
}

/** Every stock one fund holds — the fund page's Largest holdings and the Rankings dialog. */
export const fundHoldingsQueryOptions = (fund: string, period: string | null) =>
  queryOptions<FundHoldings | null>({
    queryKey: fundKeys.fundHoldings(fund, period),
    queryFn: () => orNullOn404(() => fundService.fundHoldings(fund, period)),
    staleTime: FUND_DATA_STALE_MS,
  })

export const useFundHoldings = (
  fund: string,
  period: string | null,
): UseQueryResult<FundHoldings | null> =>
  useQuery({ ...fundHoldingsQueryOptions(fund, period), enabled: fund.length > 0 })

/** Every fund holding one stock — Industry holdings' "Who holds X". Silent with no symbol. */
export const stockHoldersQueryOptions = (symbol: string, period: string | null) =>
  queryOptions<StockHolders | null>({
    queryKey: fundKeys.stockHolders(symbol, period),
    queryFn: () => orNullOn404(() => fundService.stockHolders(symbol, period)),
    staleTime: FUND_DATA_STALE_MS,
  })

export const useStockHolders = (
  symbol: string,
  period: string | null,
): UseQueryResult<StockHolders | null> =>
  useQuery({
    ...stockHoldersQueryOptions(symbol, period),
    enabled: symbol.length > 0,
    placeholderData: keepPreviousData,
  })

/** The fund page's NAV tab: a year of monthly filings. */
export const FUND_NAV_MONTHS = 12

export const fundNavQueryOptions = (fund: string) =>
  queryOptions<NavHistory | null>({
    queryKey: fundKeys.navHistory(fund, FUND_NAV_MONTHS),
    queryFn: () => orNullOn404(() => fundService.navHistory(fund, FUND_NAV_MONTHS)),
    staleTime: FUND_DATA_STALE_MS,
  })

export const useFundNav = (fund: string): UseQueryResult<NavHistory | null> =>
  useQuery(fundNavQueryOptions(fund))

/** The fund page's Income statement tab (mm-be #376). */
export const fundIncomeStatementQueryOptions = (fund: string) =>
  queryOptions<FundIncomeStatement | null>({
    queryKey: fundKeys.incomeStatement(fund),
    queryFn: () => orNullOn404(() => fundService.incomeStatement(fund)),
    staleTime: FUND_DATA_STALE_MS,
  })

export const useFundIncomeStatement = (fund: string): UseQueryResult<FundIncomeStatement | null> =>
  useQuery(fundIncomeStatementQueryOptions(fund))

// ── Dividends ────────────────────────────────────────────────────────────────

/** Dividends' main read: both lists in one payload. No parameters. */
export const fundDividendsQueryOptions = () =>
  queryOptions<FundDividends>({
    queryKey: fundKeys.dividends(),
    queryFn: () => fundService.dividends(),
    staleTime: FUND_DATA_STALE_MS,
  })

export const useSuspenseFundDividends = (): UseSuspenseQueryResult<FundDividends> =>
  useSuspenseQuery(fundDividendsQueryOptions())

/** The same payload, for a fund's page, where the dividend figure must not hold the page up. */
export const useFundDividends = (): UseQueryResult<FundDividends> =>
  useQuery(fundDividendsQueryOptions())
